import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { TIPOS_FORNECEDOR, TIPOS_FORNECEDOR_LABELS, STATUS_RELACIONAMENTO_LABELS } from '@/pages/painel/lib/inteligencia';
import FornecedorForm from './FornecedorForm';
import FornecedorPrecos from './FornecedorPrecos';
import FornecedorComparador from './FornecedorComparador';

interface Fornecedor {
  id: number;
  empresa_id: number | null;
  tipo_fornecedor: string[];
  categorias: string[];
  marcas: string[];
  produtos: string[];
  politica_revenda: string | null;
  desconto: number | null;
  frete: string | null;
  prazo: string | null;
  pedido_minimo: string | null;
  condicao_pagamento: string | null;
  regiao: string | null;
  score_fornecedor: number | null;
  status_relacionamento: string;
  empresas: { nome: string; cidade: string | null; uf: string | null; cnpj: string | null } | null;
}

interface EmpresaOpt {
  id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
}

export default function FornecedoresModule() {
  const { password } = useOutletContext<{ password: string }>();
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<Fornecedor | null>(null);
  const [precosFor, setPrecosFor] = useState<Fornecedor | null>(null);
  const [comparadorOpen, setComparadorOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'fornecedor_listar', password, busca, tipo_fornecedor: tipoFilter || undefined, status_relacionamento: statusFilter || undefined },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (data?.error) throw new Error(data.error);
      setFornecedores(data?.fornecedores ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar fornecedores.');
    } finally {
      setLoading(false);
    }
  };

  const loadEmpresas = async () => {
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'list_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (data?.error) throw new Error(data.error);
      const lista = (data?.empresas ?? []) as any[];
      setEmpresas(lista.map((e) => ({ id: e.id, nome: e.nome, cidade: e.cidade, uf: e.uf })));
    } catch {
      // silencioso — o seletor fica vazio e o usuário pode recarregar
    }
  };

  useEffect(() => {
    load();
    loadEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, tipoFilter, statusFilter]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return fornecedores;
    return fornecedores.filter((f) => {
      const alvo = [f.empresas?.nome, f.marcas.join(' '), f.produtos.join(' '), f.categorias.join(' '), f.regiao]
        .filter(Boolean).join(' ').toLowerCase();
      return alvo.includes(q);
    });
  }, [fornecedores, busca]);

  const excluir = async (f: Fornecedor) => {
    await supabase.functions.invoke('filas-admin', {
      body: { action: 'fornecedor_excluir', password, id: f.id },
    });
    await load();
  };

  const abrirNovo = () => {
    setEditando(null);
    setFormOpen(true);
  };

  const abrirEditar = (f: Fornecedor) => {
    setEditando(f);
    setFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-store-3-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Fornecedores</h2>
            <p className="text-sm text-gray-500">
              Organize, filtre e compare fornecedores — sem duplicar empresas.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setComparadorOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-scales-3-line"></i>
            Comparar
          </button>
          <button
            onClick={abrirNovo}
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#E8610A] text-white px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Novo fornecedor
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Quem vende cadeiras? Cavaletti? Fabricante? Pernambuco?"
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
          />
        </div>
        <select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
          <option value="">Todos os tipos</option>
          {TIPOS_FORNECEDOR.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_RELACIONAMENTO_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && fornecedores.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-3">
            <i className="ri-store-3-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm font-semibold text-gray-700">Nenhum fornecedor cadastrado</p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
            Cadastre fornecedores vinculando a uma empresa existente. Eles aparecerão nas filas de inteligência.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtrados.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{f.empresas?.nome ?? 'Fornecedor'}</h3>
                  <p className="text-xs text-gray-500">{f.empresas?.cidade ?? ''}{f.empresas?.uf ? ` - ${f.empresas.uf}` : ''}</p>
                </div>
                {f.score_fornecedor != null && (
                  <span className="shrink-0 text-xs font-bold text-gray-800 bg-gray-50 rounded-lg px-2 py-1">{f.score_fornecedor}</span>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {f.tipo_fornecedor.map((t) => (
                  <span key={t} className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                    {TIPOS_FORNECEDOR_LABELS[t] ?? t}
                  </span>
                ))}
                <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  f.status_relacionamento === 'ativo' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {STATUS_RELACIONAMENTO_LABELS[f.status_relacionamento] ?? f.status_relacionamento}
                </span>
              </div>

              {(f.marcas.length > 0 || f.produtos.length > 0) && (
                <div className="mt-3 text-xs text-gray-600 space-y-1">
                  {f.marcas.length > 0 && <p><span className="text-gray-400">Marcas:</span> {f.marcas.join(', ')}</p>}
                  {f.produtos.length > 0 && <p><span className="text-gray-400">Produtos:</span> {f.produtos.join(', ')}</p>}
                </div>
              )}

              <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-gray-500">
                {f.desconto != null && <span>{f.desconto}% desc.</span>}
                {f.prazo && <span>Prazo {f.prazo}</span>}
                {f.regiao && <span>{f.regiao}</span>}
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                <button onClick={() => abrirEditar(f)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1.5 rounded-lg hover:bg-[#E8610A]/10 cursor-pointer whitespace-nowrap">
                  <i className="ri-edit-line"></i> Editar
                </button>
                <button onClick={() => setPrecosFor(f)} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800 px-2 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap">
                  <i className="ri-price-tag-3-line"></i> Preços
                </button>
                <button onClick={() => excluir(f)} className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer">
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <FornecedorForm
          empresas={empresas}
          inicial={editando}
          password={password}
          onSaved={() => { setFormOpen(false); load(); }}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {precosFor && (
        <FornecedorPrecos
          fornecedorId={precosFor.id}
          fornecedorNome={precosFor.empresas?.nome ?? 'Fornecedor'}
          password={password}
          onClose={() => setPrecosFor(null)}
        />
      )}

      {comparadorOpen && (
        <FornecedorComparador fornecedores={fornecedores} password={password} onClose={() => setComparadorOpen(false)} />
      )}
    </div>
  );
}